import Highcharts from "highcharts";
import exporting from "highcharts/modules/exporting";
import offlineExporting from "highcharts/modules/offline-exporting";
import exportData from "highcharts/modules/export-data";
import dayjs from "dayjs";
import { Canvg } from "canvg";
import { getDateFormatset } from "utils";

exporting(Highcharts);
offlineExporting(Highcharts);
exportData(Highcharts);

export const createExportingOptions = ({ chartTitle }: { chartTitle: string }) => ({
  enabled: true,
  filename: `${chartTitle.replace(/\s+/g, '_')}_${dayjs().format(getDateFormatset())}`,
  fallbackToExportServer: false,
  sourceWidth: 1600,
  sourceHeight: 900,
  chartOptions: {
    title: { text: chartTitle },
    subtitle: { text: "Exported from Highcharts" },
    credits: { enabled: false },
  },
  buttons: {
    contextButton: {
      menuItems: [
        {
          text: "Download PNG",
          async onclick(this: Highcharts.Chart) {
            const chart = this;

            // Get export dimensions
            const srcW = chart.options.exporting?.sourceWidth ?? 1600;
            const srcH = chart.options.exporting?.sourceHeight ?? 900;

            // Get SVG of the chart with those dimensions
            const svg = chart.getSVG({
              exporting: { sourceWidth: srcW, sourceHeight: srcH, scale: 1 },
            });

            // Canvas with scale for high definition
            const pixelRatio = Math.max(1, window.devicePixelRatio || 1);
            const canvas = document.createElement("canvas");
            canvas.width = Math.floor(srcW * pixelRatio);
            canvas.height = Math.floor(srcH * pixelRatio);

            const ctx = canvas.getContext("2d");
            if (!ctx) return;
            // When scaling the context, we draw “in CSS units”
            ctx.scale(pixelRatio, pixelRatio);

            // Render the SVG directly to the canvas (without img.src)
            const v = Canvg.fromString(ctx, svg, {
              ignoreMouse: true,
              ignoreAnimation: true,
              // useCORS: true, // optional if you include external images in the SVG
            });
            await v.render();

            // Download as PNG
            canvas.toBlob((blob) => {
              if (!blob) return;
              const link = document.createElement("a");
              link.href = URL.createObjectURL(blob);
              link.download = `${chart.options.exporting?.filename ?? "chart"}.png`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(link.href);
            }, "image/png");
          },
        },
        {
          text: "Download JPEG",
          async onclick(this: Highcharts.Chart) {
            const chart = this;

            const srcW = chart.options.exporting?.sourceWidth ?? 1600;
            const srcH = chart.options.exporting?.sourceHeight ?? 900;

            const svg = chart.getSVG({
              exporting: { sourceWidth: srcW, sourceHeight: srcH, scale: 1 },
            });

            const pixelRatio = Math.max(1, window.devicePixelRatio || 1);
            const canvas = document.createElement("canvas");
            canvas.width = Math.floor(srcW * pixelRatio);
            canvas.height = Math.floor(srcH * pixelRatio);

            const ctx = canvas.getContext("2d");
            if (!ctx) return;
            ctx.scale(pixelRatio, pixelRatio);

            // White background (JPEG does not support transparency)
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, srcW, srcH);

            const v = Canvg.fromString(ctx, svg, {
              ignoreMouse: true,
              ignoreAnimation: true,
            });
            await v.render();

            canvas.toBlob((blob) => {
              if (!blob) return;
              const link = document.createElement("a");
              link.href = URL.createObjectURL(blob);
              link.download = `${chart.options.exporting?.filename ?? "chart"}.jpeg`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(link.href);
            }, "image/jpeg", 0.92);
          },
        },
        {
          text: "Download SVG",
          async onclick(this: Highcharts.Chart) {
            this.exportChart({ type: 'image/svg+xml' }, {});
          }
        },
        "separator",
        "downloadCSV",
        "separator",
        {
          text: "Download PDF",
          async onclick(this: Highcharts.Chart) {
            this.exportChart({ type: 'application/pdf' }, {});
          }
        }
      ],
    },
  },
});
